import { useState } from "react";
import {
  BHS_INSTRUMENT_VERSION,
  BHS_ITEMS,
  WORK_MISSED_OPTIONS,
  WORK_MISSED_QUESTION,
  WORK_PRODUCTIVITY_ANCHORS,
  WORK_PRODUCTIVITY_QUESTION,
} from "@backcoach/domain";
import { useApi, unwrap } from "../client.js";
import type { WorkdayArea, WorkMissed } from "@backcoach/domain";
import type { BhsResultView } from "../hooks.js";
import Questionnaire from "./Questionnaire.js";
import type { Question } from "./Questionnaire.js";

/**
 * The Back Health Score. Eleven questions, nine scored plus two context, one
 * per screen, tap only, then one or two unscored work impact questions
 * (D-51). Scored on the server; the client sends option scores and never
 * sees a formula.
 */
const GROUP: Record<string, string> = {
  context: "Workday context",
  symptoms: "Current symptoms",
  confidence: "Movement confidence",
  lifestyle: "Sitting and lifestyle",
  recovery: "Recovery pattern",
};

const QUESTIONS: Question[] = [
  ...BHS_ITEMS.map((item) => {
    const group = GROUP[item.construct];
    return {
      id: item.id,
      ...(group ? { group } : {}),
      text: item.question,
      options: item.options.map((o) => ({ value: item.construct === "context" ? (o.key ?? o.label) : String(o.score), label: o.label })),
    };
  }),
  {
    id: "w1_productivity",
    group: "Work impact",
    text: WORK_PRODUCTIVITY_QUESTION,
    scale: WORK_PRODUCTIVITY_ANCHORS,
    options: Array.from({ length: 11 }, (_, n) => ({
      value: String(n),
      label: n === 0 ? `0, ${WORK_PRODUCTIVITY_ANCHORS.low.toLowerCase()}` : n === 10 ? `10, ${WORK_PRODUCTIVITY_ANCHORS.high}` : String(n),
    })),
  },
  {
    id: "w2_missed",
    group: "Work impact",
    text: WORK_MISSED_QUESTION,
    options: WORK_MISSED_OPTIONS.map((o) => ({ value: o.key, label: o.label })),
    skipIf: (a) => a["w1_productivity"] === "0",
  },
];

export default function Assessment({
  screeningAttemptId,
  onDone,
  onExit,
}: {
  screeningAttemptId: string;
  onDone: (result: BhsResultView) => void;
  onExit: () => void;
}) {
  const api = useApi();
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      {error && <p className="bc-note">{error}</p>}
      <Questionnaire
        title="Back Health Score"
        questions={QUESTIONS}
        onExit={onExit}
        onComplete={(a) => {
          const answers: Record<string, number> = {};
          for (const item of BHS_ITEMS) {
            if (item.construct === "context") continue;
            answers[item.id] = Number(a[item.id] ?? 0);
          }
          const productivity = Number(a["w1_productivity"] ?? 0);
          const workImpact = { productivity, missed: productivity === 0 ? null : ((a["w2_missed"] ?? "none") as WorkMissed) };
          void (async () => {
            try {
              const r = unwrap(
                await api.POST("/v1/me/bhs", {
                  body: {
                    answers,
                    context: {
                      workdayArea: (a["workday_area"] ?? "none") as WorkdayArea,
                      workdayTiming: a["workday_timing"] ?? "during_day",
                    },
                    workImpact,
                    instrumentVersion: BHS_INSTRUMENT_VERSION,
                    screeningAttemptId,
                  },
                }),
              );
              onDone(r);
            } catch (e) {
              setError(e instanceof Error ? e.message : "Could not record that");
            }
          })();
        }}
      />
    </>
  );
}
